/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { useEffect, useRef } from 'react'
import { message } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { applyTeamFinderApiError } from '@sportninja/common/features/teamFinder/applyTeamFinderApiError'
import { hasAnyFilter } from '@sportninja/common/features/teamFinder/filterParams'
import TeamFinderHeader from './components/TeamFinderHeader'
import TeamFinderFilters from './components/TeamFinderFilters'
import TeamFinderResults from './components/TeamFinderResults'
import { useFilterUrlSync } from './hooks/useFilterUrlSync'
import { useTeamFinderForm } from './hooks/useTeamFinderForm'
import { useTeamFinderSearch } from './hooks/useTeamFinderSearch'

const TeamFinder = () => {
  'use memo'

  const form = useTeamFinderForm()
  const urlSync = useFilterUrlSync()

  const {
    search,
    loadMore,
    results,
    isLoading,
    isLoadingMore,
    hasMore,
    hasSearched,
  } = useTeamFinderSearch({
    onError: (err) =>
      applyTeamFinderApiError(err, {
        setFieldErrors: form.setFieldErrors,
        notifyError: message.error,
      }),
  })

  const handleSearch = () => {
    // The URL is written here, not on every edit, so it always describes the
    // results actually on screen.
    urlSync.writeFilters(form.filters)
    search(form.filters)
  }

  const handleClear = () => {
    form.clearFilters()
    urlSync.clearFilters()
  }

  /*
   * A shared link should land on results, not on a filled-in form the recipient
   * still has to submit.
   *
   * Whether this page load WAS a deep link is decided once, at mount, and held
   * in a ref. Deriving it from the live filters instead would misfire on a
   * fresh screen: filters start empty, so the effect skips without latching,
   * and the user's first pick then looks exactly like an arriving deep link —
   * firing a search they never asked for.
   */
  const deepLinkFilters = useRef(
    hasAnyFilter(form.filters) ? form.filters : null
  )
  useEffect(() => {
    const filters = deepLinkFilters.current
    if (!filters) {
      return
    }
    // Cleared immediately so this can never run twice — the search rewrites
    // the URL, which re-renders this component.
    deepLinkFilters.current = null
    search(filters)
    // Mount only: a later filter edit is the user typing, not a link arriving.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div css={pageStyle}>
      <div css={containerStyle}>
        <TeamFinderHeader />
        <div css={cardStyle}>
          <TeamFinderFilters
            form={form}
            onSearch={handleSearch}
            onClear={handleClear}
            isLoading={isLoading}
          />
        </div>
        {/* Separator between the filter bar and results, matching My Schedule. */}
        <div css={separatorStyle} />
        {hasSearched || isLoading ? (
          <TeamFinderResults
            results={results}
            isLoading={isLoading}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={loadMore}
          />
        ) : null}
      </div>
    </div>
  )
}

export default TeamFinder

const pageStyle = css`
  min-height: 100vh;
  background-color: ${colors.SECONDARY_500};
  margin-bottom: 100px;
`
// Full-width layout matching the My Schedule page: wide max-width, generous
// horizontal gutters, no boxed card around the filters.
const containerStyle = css`
  max-width: 1600px;
  margin: 0 auto;
  padding: 48px 88px;
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 900px) {
    padding: 24px;
  }
`
// Boxed container for the filter form.
const cardStyle = css`
  background-color: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 12px;
  padding: 24px;
`
const separatorStyle = css`
  height: 1px;
  background-color: ${colors.SECONDARY_200};
`
